import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CloudAlert,
  CloudCheck,
  Eraser,
  ImageIcon,
  LoaderCircle,
  Pencil,
  Redo2,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import QuestionImage from "./QuestionImage";
import {
  DRAWING_HEIGHT,
  DRAWING_LIMITS,
  DRAWING_WIDTH,
  appendPoint,
  hasPenStroke,
  renderDrawing,
  simplifyPoints,
  strokeWidth,
} from "../utils/drawingFormat";
import "./DrawingPad.css";

const SIZE_LABELS = ["Thin", "Medium", "Thick"];
const timeLabel = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");
const toDrawing = (strokes) => ({ version: 1, width: DRAWING_WIDTH, height: DRAWING_HEIGHT, strokes });
const totalPoints = (strokes) => strokes.reduce((sum, stroke) => sum + stroke.points.length / 2, 0);

// Pixels with any ink left, so a drawing whose strokes were all erased
// counts as blank. Falls back to the stroke check where canvas pixels are
// unavailable.
function canvasHasInk(canvas, strokes) {
  const context = canvas?.getContext?.("2d");
  if (!context || !canvas.width) return hasPenStroke(toDrawing(strokes));
  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
  for (let index = 3; index < data.length; index += 4) if (data[index] !== 0) return true;
  return false;
}

function SyncStatus({ pad }) {
  const { syncState, lastSavedAt, localStored } = pad;
  let icon = <CloudCheck size={17} aria-hidden="true" />;
  let text = lastSavedAt ? `Draft saved to StudyDen at ${timeLabel(lastSavedAt)}` : "No unsaved changes";
  if (syncState === "pending") {
    icon = <CloudAlert size={17} aria-hidden="true" />;
    text = localStored ? "Changes not sent yet — kept on this device" : "Changes not sent yet";
  } else if (syncState === "saving") {
    icon = <LoaderCircle size={17} aria-hidden="true" className="eq-assess-spin" />;
    text = "Saving draft…";
  } else if (syncState === "offline" || syncState === "failed") {
    icon = <CloudAlert size={17} aria-hidden="true" />;
    text = localStored
      ? "Can't reach StudyDen — changes are kept on this device and will be sent when the connection returns"
      : "Can't reach StudyDen — changes are only on this page";
  }
  return <p className={`dp-sync is-${syncState}`} role="status" aria-live="polite">{icon}<span>{text}</span></p>;
}

// Drawing dialog. Strokes, undo and redo are local; every change is reported
// through onChange so the page can keep it on this device and checkpoint it.
const DrawingPad = ({
  pad,
  question,
  quizId,
  mediaContext,
  onChange,
  onSave,
  onCancel,
  onKeepEditing,
  onTakeOver,
  onClose,
  onRetryOpen,
  onReplaceWithMine,
  onAdoptRecovered,
  onDiscardRecovered,
}) => {
  const [history, setHistory] = useState({ strokes: [], undo: [], redo: [] });
  const [tool, setTool] = useState("pen");
  const [size, setSize] = useState(1);
  const [confirmClear, setConfirmClear] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [limitMessage, setLimitMessage] = useState(null);
  const dialogRef = useRef(null);
  const canvasRef = useRef(null);
  const surfaceRef = useRef(null);
  const liveRef = useRef(null);
  const strokesRef = useRef([]);
  const scaleRef = useRef(1);
  const historyRef = useRef(history);
  historyRef.current = history;
  strokesRef.current = history.strokes;

  const editable = pad.phase === "editing" || pad.phase === "conflict";
  const headingId = `dp-heading-${question.id}`;
  const hasMedia = Boolean(question.questionImageURL || question.questionMedia);

  // A new starting drawing (opened, or recovered from this device) replaces
  // the canvas; adopting a recovered drawing can be undone.
  useEffect(() => {
    if (!pad.initialDrawing) return;
    setHistory((previous) => ({
      strokes: pad.initialDrawing.strokes,
      undo: previous.strokes.length > 0 || previous.undo.length > 0 ? [...previous.undo, previous.strokes] : [],
      redo: [],
    }));
  }, [pad.initialDrawing]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext?.("2d");
    if (!context) return;
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    renderDrawing(context, toDrawing(strokesRef.current), scaleRef.current);
  }, []);

  // The canvas backing store follows the displayed size and device pixel
  // ratio; strokes are stored in logical units, so resizing, rotating or
  // changing display density only re-renders them.
  useLayoutEffect(() => {
    const surface = surfaceRef.current;
    const canvas = canvasRef.current;
    if (!surface || !canvas) return undefined;
    const resize = () => {
      const width = surface.clientWidth;
      if (!width) return;
      const ratio = window.devicePixelRatio || 1;
      const pixelWidth = Math.round(width * ratio);
      if (canvas.width === pixelWidth && scaleRef.current === pixelWidth / DRAWING_WIDTH) return;
      canvas.width = pixelWidth;
      canvas.height = Math.round((width * DRAWING_HEIGHT / DRAWING_WIDTH) * ratio);
      scaleRef.current = canvas.width / DRAWING_WIDTH;
      redraw();
    };
    resize();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(resize) : null;
    observer?.observe(surface);
    window.addEventListener("resize", resize);
    // A zoom or a move to another screen can change the pixel ratio without
    // any resize event, so the size is also re-checked once a second.
    const densityCheck = window.setInterval(resize, 1000);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", resize);
      window.clearInterval(densityCheck);
    };
  }, [redraw, pad.phase]);

  useEffect(() => { redraw(); }, [history.strokes, redraw]);

  // Dialog behaviour: focus inside on open, keep Tab inside, lock page scroll.
  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector("[data-autofocus]")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus && typeof previousFocus.focus === "function") previousFocus.focus();
    };
  }, []);

  const commitStrokes = useCallback((nextStrokes) => {
    setHistory((previous) => ({ strokes: nextStrokes, undo: [...previous.undo, previous.strokes], redo: [] }));
    onChange(toDrawing(nextStrokes));
  }, [onChange]);

  const undo = useCallback(() => {
    const previous = historyRef.current;
    if (!editable || previous.undo.length === 0) return;
    const strokes = previous.undo[previous.undo.length - 1];
    setHistory({ strokes, undo: previous.undo.slice(0, -1), redo: [...previous.redo, previous.strokes] });
    onChange(toDrawing(strokes));
  }, [editable, onChange]);

  const redo = useCallback(() => {
    const previous = historyRef.current;
    if (!editable || previous.redo.length === 0) return;
    const strokes = previous.redo[previous.redo.length - 1];
    setHistory({ strokes, undo: [...previous.undo, previous.strokes], redo: previous.redo.slice(0, -1) });
    onChange(toDrawing(strokes));
  }, [editable, onChange]);

  const clearAll = () => {
    setConfirmClear(false);
    if (strokesRef.current.length > 0) commitStrokes([]);
  };

  const logicalPoint = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return [
      ((event.clientX - rect.left) / rect.width) * DRAWING_WIDTH,
      ((event.clientY - rect.top) / rect.height) * DRAWING_HEIGHT,
    ];
  };

  const drawSegment = (stroke, fromIndex) => {
    const context = canvasRef.current?.getContext?.("2d");
    if (!context) return;
    const scale = scaleRef.current;
    const { points } = stroke;
    context.save();
    context.globalCompositeOperation = stroke.tool === "eraser" ? "destination-out" : "source-over";
    context.strokeStyle = "#111827";
    context.fillStyle = "#111827";
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = strokeWidth(stroke) * scale;
    context.beginPath();
    if (fromIndex === 0) {
      context.arc(points[0] * scale, points[1] * scale, (strokeWidth(stroke) * scale) / 2, 0, Math.PI * 2);
      context.fill();
    } else {
      context.moveTo(points[fromIndex - 2] * scale, points[fromIndex - 1] * scale);
      context.lineTo(points[fromIndex] * scale, points[fromIndex + 1] * scale);
      context.stroke();
    }
    context.restore();
  };

  const handlePointerDown = (event) => {
    if (!editable || (event.pointerType === "mouse" && event.button !== 0) || liveRef.current) return;
    if (strokesRef.current.length >= DRAWING_LIMITS.maxStrokes || totalPoints(strokesRef.current) >= DRAWING_LIMITS.maxTotalPoints) {
      setLimitMessage("This drawing has reached its size limit. Undo or clear some strokes to keep drawing.");
      return;
    }
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const stroke = { tool, size, points: [] };
    appendPoint(stroke.points, ...logicalPoint(event));
    liveRef.current = { pointerId: event.pointerId, stroke };
    drawSegment(stroke, 0);
  };

  const handlePointerMove = (event) => {
    const live = liveRef.current;
    if (!live || live.pointerId !== event.pointerId) return;
    event.preventDefault();
    const events = typeof event.getCoalescedEvents === "function" ? event.getCoalescedEvents() : [];
    for (const sample of events.length > 0 ? events : [event]) {
      if (live.stroke.points.length / 2 >= DRAWING_LIMITS.maxPointsPerStroke) break;
      if (appendPoint(live.stroke.points, ...logicalPoint(sample))) drawSegment(live.stroke, live.stroke.points.length - 2);
    }
  };

  const finishStroke = (event) => {
    const live = liveRef.current;
    if (!live || live.pointerId !== event.pointerId) return;
    liveRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const points = simplifyPoints(live.stroke.points);
    if (totalPoints(strokesRef.current) + points.length / 2 > DRAWING_LIMITS.maxTotalPoints) {
      setLimitMessage("This drawing has reached its size limit. Undo or clear some strokes to keep drawing.");
      redraw();
      return;
    }
    setLimitMessage(null);
    commitStrokes([...strokesRef.current, { ...live.stroke, points }]);
  };

  const handleKeyDown = (event) => {
    const modifier = event.ctrlKey || event.metaKey;
    if (modifier && event.key.toLowerCase() === "z") {
      event.preventDefault();
      if (event.shiftKey) redo(); else undo();
    } else if (modifier && event.key.toLowerCase() === "y") {
      event.preventDefault();
      redo();
    } else if (event.key === "Tab") {
      const focusable = [...dialogRef.current.querySelectorAll("button:not([disabled]), [href], [tabindex]:not([tabindex='-1'])")];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  };

  const save = () => onSave({ hasInk: canvasHasInk(canvasRef.current, strokesRef.current) });
  const busy = pad.phase === "saving" || pad.phase === "cancelling" || pad.phase === "opening";
  const showCanvas = !["takeover", "error"].includes(pad.phase);

  return (
    <div className="dp-backdrop">
      <div
        className="dp-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        ref={dialogRef}
        onKeyDown={handleKeyDown}
      >
        <header className="dp-header">
          <div>
            <h2 id={headingId}>Draw your answer</h2>
            {question.questionText && <p className="dp-question">{question.questionText}</p>}
          </div>
          {hasMedia && (
            <button
              type="button"
              className="dp-media-toggle"
              aria-expanded={showMedia}
              onClick={() => setShowMedia((value) => !value)}
            >
              <ImageIcon size={17} aria-hidden="true" />{showMedia ? "Hide question image" : "Show question image"}
            </button>
          )}
        </header>
        {hasMedia && showMedia && (
          <div className="dp-media"><QuestionImage question={question} quizId={quizId} context={mediaContext} /></div>
        )}

        {pad.phase === "opening" && (
          <div className="dp-state" role="status"><LoaderCircle size={22} className="eq-assess-spin" aria-hidden="true" />Opening your drawing…</div>
        )}

        {pad.phase === "takeover" && (
          <div className="dp-alert is-warning" role="alert">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>
              <strong>This drawing is open on another device or window.</strong>
              <p>
                {pad.other?.editing?.openedAt ? `It was opened at ${timeLabel(pad.other.editing.openedAt)}. ` : ""}
                Taking over here closes it there. Changes there that haven't reached StudyDen yet won't be included;
                {pad.other?.draft ? " the last draft it saved to StudyDen will open here." : " it has not saved a draft yet."}
              </p>
              <div className="dp-alert-actions">
                <button type="button" className="eq-assess-primary" data-autofocus onClick={onTakeOver}>Take over here</button>
                <button type="button" className="eq-assess-secondary" onClick={onClose}>Not now</button>
              </div>
            </div>
          </div>
        )}

        {pad.phase === "error" && (
          <div className="dp-alert is-error" role="alert">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>
              <strong>{pad.message}</strong>
              <div className="dp-alert-actions">
                <button type="button" className="eq-assess-primary" data-autofocus onClick={onRetryOpen}>Try again</button>
                <button type="button" className="eq-assess-secondary" onClick={onClose}>Close</button>
              </div>
            </div>
          </div>
        )}

        {pad.recoveryOffer && (
          <div className="dp-alert is-warning" role="alert">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>
              <strong>Unsaved changes from this device were found.</strong>
              <p>
                They are from {timeLabel(pad.recoveryOffer.updatedAt) || "earlier"} and never reached StudyDen
                {pad.recoveryOffer.olderBase ? ", and were based on an older saved drawing" : ""}.
              </p>
              <div className="dp-alert-actions">
                <button type="button" className="eq-assess-secondary" onClick={onAdoptRecovered}>Use them</button>
                <button type="button" className="eq-assess-secondary" onClick={onDiscardRecovered}>Discard them</button>
              </div>
            </div>
          </div>
        )}

        {pad.notice && pad.phase === "editing" && <p className="dp-notice" role="status">{pad.notice}</p>}

        {pad.phase === "conflict" && (
          <div className="dp-alert is-warning" role="alert">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>
              <strong>This drawing was saved on another device or window after you opened it.</strong>
              <p>Choose which drawing to keep. Nothing has been replaced yet.</p>
              <div className="dp-alert-actions">
                <button type="button" className="eq-assess-secondary" onClick={onReplaceWithMine}>Replace it with this drawing</button>
                <button type="button" className="eq-assess-secondary" onClick={onCancel}>Keep the other version</button>
              </div>
            </div>
          </div>
        )}

        {pad.phase === "superseded" && (
          <div className="dp-alert is-warning" role="alert">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>
              <strong>
                {pad.reason === "cancelled"
                  ? "This edit was cancelled, so changes here can no longer be saved."
                  : "This drawing was opened on another device or window, so changes here can no longer be saved."}
              </strong>
              <p>Your drawing is still here and kept on this device.</p>
              <div className="dp-alert-actions">
                <button type="button" className="eq-assess-primary" data-autofocus onClick={onTakeOver}>
                  {pad.reason === "cancelled" ? "Start a new edit with this drawing" : "Take over and keep this drawing"}
                </button>
                <button type="button" className="eq-assess-secondary" onClick={onClose}>Close</button>
              </div>
            </div>
          </div>
        )}

        {pad.phase === "cancel-pending" && (
          <div className="dp-alert is-error" role="alert">
            <AlertTriangle size={20} aria-hidden="true" />
            <div>
              <strong>Cancellation not confirmed yet.</strong>
              <p>
                StudyDen hasn't confirmed that these changes were discarded. We'll keep trying while this page is open.
                If time runs out before the cancellation gets through, the last draft StudyDen saved may be submitted.
              </p>
              <div className="dp-alert-actions">
                <button type="button" className="eq-assess-secondary" data-autofocus onClick={onCancel}>Retry cancel</button>
                <button type="button" className="eq-assess-secondary" onClick={onKeepEditing}>Keep editing</button>
              </div>
            </div>
          </div>
        )}

        {(pad.message || limitMessage) && ["editing", "conflict"].includes(pad.phase) && (
          <p className="dp-message" role="alert">{pad.message || limitMessage}</p>
        )}

        {showCanvas && (
          <>
            <div className="dp-toolbar" role="toolbar" aria-label="Drawing tools">
              <button type="button" className={`dp-tool${tool === "pen" ? " is-active" : ""}`} aria-pressed={tool === "pen"}
                onClick={() => setTool("pen")} disabled={!editable} data-autofocus>
                <Pencil size={18} aria-hidden="true" />Pen
              </button>
              <button type="button" className={`dp-tool${tool === "eraser" ? " is-active" : ""}`} aria-pressed={tool === "eraser"}
                onClick={() => setTool("eraser")} disabled={!editable}>
                <Eraser size={18} aria-hidden="true" />Eraser
              </button>
              <button type="button" className="dp-tool" onClick={undo} disabled={!editable || history.undo.length === 0}
                aria-keyshortcuts="Control+Z">
                <Undo2 size={18} aria-hidden="true" />Undo
              </button>
              <button type="button" className="dp-tool" onClick={redo} disabled={!editable || history.redo.length === 0}
                aria-keyshortcuts="Control+Y">
                <Redo2 size={18} aria-hidden="true" />Redo
              </button>
              <button type="button" className="dp-tool" onClick={() => setConfirmClear(true)}
                disabled={!editable || history.strokes.length === 0}>
                <Trash2 size={18} aria-hidden="true" />Clear
              </button>
              <span className="dp-divider" aria-hidden="true" />
              <div className="dp-sizes" role="radiogroup" aria-label="Line width">
                {SIZE_LABELS.map((label, index) => (
                  <button
                    key={label}
                    type="button"
                    role="radio"
                    aria-checked={size === index}
                    aria-label={label}
                    title={label}
                    className={`dp-size${size === index ? " is-active" : ""}`}
                    onClick={() => setSize(index)}
                    disabled={!editable}
                  >
                    <i style={{ width: 6 + index * 5, height: 6 + index * 5 }} aria-hidden="true" />
                  </button>
                ))}
              </div>
            </div>

            {confirmClear && (
              <div className="dp-confirm" role="alert">
                <span>Clear the whole drawing? You can undo this.</span>
                <button type="button" className="eq-assess-secondary" onClick={clearAll}>Clear drawing</button>
                <button type="button" className="eq-assess-secondary" onClick={() => setConfirmClear(false)}>Keep it</button>
              </div>
            )}

            <p className="dp-hint">Draw clearly and label your diagram. Use Undo or the eraser to fix mistakes.</p>
            <div className={`dp-surface${editable ? "" : " is-locked"}${tool === "eraser" ? " is-erasing" : ""}`} ref={surfaceRef}>
              <canvas
                ref={canvasRef}
                className="dp-canvas"
                role="img"
                aria-label="Drawing canvas. Draw with a mouse, touch screen or stylus."
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={finishStroke}
                onPointerCancel={finishStroke}
                onContextMenu={(event) => event.preventDefault()}
              />
              {pad.phase === "cancelling" && (
                <div className="dp-overlay" role="status"><LoaderCircle size={22} className="eq-assess-spin" aria-hidden="true" />Discarding changes — waiting for StudyDen to confirm…</div>
              )}
              {pad.phase === "saving" && (
                <div className="dp-overlay" role="status"><LoaderCircle size={22} className="eq-assess-spin" aria-hidden="true" />Saving your drawing…</div>
              )}
            </div>
          </>
        )}

        <footer className="dp-footer">
          <div className="dp-footer-text">
            {showCanvas && !["opening", "cancelling", "cancel-pending"].includes(pad.phase) ? <SyncStatus pad={pad} /> : null}
            <p>Use your mouse, touch or pen. Your answer only changes when you choose Save drawing.</p>
          </div>
          {showCanvas && (
            <div className="dp-footer-actions">
              <button type="button" className="eq-assess-secondary" onClick={pad.phase === "superseded" ? onClose : onCancel}
                disabled={busy || pad.phase === "cancel-pending"}>
                {pad.phase === "superseded" ? "Close" : "Cancel"}
              </button>
              <button type="button" className="eq-assess-primary dp-save" onClick={save}
                disabled={!editable || pad.phase === "conflict"} aria-busy={pad.phase === "saving"}>
                {pad.phase === "saving" ? "Saving…" : "Save drawing"}
              </button>
            </div>
          )}
        </footer>
        {showCanvas && pad.phase !== "superseded" && (
          <button type="button" className="dp-close" aria-label="Cancel drawing" onClick={onCancel}
            disabled={busy || pad.phase === "cancel-pending"}>
            <X size={20} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export default DrawingPad;
